import { useRef, useState } from "react";
import { Plus, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg, img, LINES, NEEDS } from "@/lib/api";

const EMPTY = { name: "", line: "complementos", needs: [], short: "", description: "", composition: "", usage: "", dose: "", images: [],
  formats: [{ label: "", price: 0, stock: 0 }], featured: false, featured_text: "", hidden: false };

const Images = ({ images, onChange }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try { const { data } = await api.post("/admin/upload", fd); onChange([...images, data.url]); toast.success("Foto subida"); }
    catch (e2) { toast.error(errMsg(e2)); }
    setBusy(false);
    e.target.value = "";
  };
  return (
    <div>
      <Label>Fotos</Label>
      <div className="flex flex-wrap gap-3 mt-2">
        {images.map((u, i) => (
          <div key={u + i} className="relative w-20 h-20 border rounded bg-slate-50">
            <img src={img(u)} alt="" className="w-full h-full object-contain" />
            <button type="button" onClick={() => onChange(images.filter((_, j) => j !== i))} data-testid={`editor-remove-image-${i}`}
              className="absolute -top-2 -right-2 bg-[#DC2626] text-white rounded-full p-0.5"><X size={14} /></button>
          </div>
        ))}
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid="editor-upload-image"
          className="w-20 h-20 border-2 border-dashed rounded grid place-items-center text-slate-500 hover:border-[#2563EB] text-xs">
          {busy ? "…" : <span className="flex flex-col items-center"><Upload size={18} />Subir</span>}
        </button>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="editor-image-file-input" />
      </div>
    </div>
  );
};

const Formats = ({ formats, onChange }) => {
  const upd = (i, k, v) => onChange(formats.map((f, j) => (j === i ? { ...f, [k]: v } : f)));
  return (
    <div>
      <Label>Formatos y precios (IVA incl.)</Label>
      <div className="space-y-2 mt-2">
        {formats.map((f, i) => (
          <div key={i} className="grid grid-cols-[1fr_120px_36px] gap-2" data-testid={`editor-format-row-${i}`}>
            <Input placeholder="Ej. 1.5L" value={f.label} onChange={(e) => upd(i, "label", e.target.value)} data-testid={`editor-format-label-${i}`} />
            <Input type="number" step="0.01" min="0" value={f.price} onChange={(e) => upd(i, "price", e.target.value)} data-testid={`editor-format-price-${i}`} />
            <button type="button" disabled={formats.length === 1} onClick={() => onChange(formats.filter((_, j) => j !== i))} className="text-slate-400 hover:text-[#DC2626] disabled:opacity-30"><Trash2 size={16} /></button>
          </div>
        ))}
        <button type="button" onClick={() => onChange([...formats, { label: "", price: 0, stock: 0 }])} data-testid="editor-add-format" className="text-sm font-bold text-[#2563EB] flex items-center gap-1"><Plus size={15} /> Añadir formato</button>
      </div>
    </div>
  );
};

export const ProductEditor = ({ product, open, onClose, onSaved }) => {
  const [p, setP] = useState(product || EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setP({ ...p, [k]: v });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...p, formats: p.formats.map((f) => ({ ...f, price: Number(f.price), stock: parseInt(f.stock, 10) || 0 })) };
    try {
      if (product) await api.put(`/admin/products/${product.slug}`, body);
      else await api.post("/admin/products", body);
      toast.success("Producto guardado");
      onSaved();
    } catch (e2) { toast.error(errMsg(e2)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="product-editor">
        <DialogHeader><DialogTitle className="font-display text-2xl text-[#0B1D3A]">{product ? "Editar producto" : "Nuevo producto"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label>Nombre</Label><Input required value={p.name} onChange={(e) => set("name")(e.target.value)} data-testid="editor-name-input" className="mt-1" /></div>
            <div><Label>Línea</Label>
              <Select value={p.line} onValueChange={set("line")}>
                <SelectTrigger className="mt-1" data-testid="editor-line-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(LINES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Necesidades</Label>
            <div className="flex flex-wrap gap-3 mt-2">{Object.entries(NEEDS).map(([k, v]) => (
              <label key={k} className="flex items-center gap-1.5 text-sm"><Checkbox checked={p.needs.includes(k)} onCheckedChange={(c) => set("needs")(c ? [...p.needs, k] : p.needs.filter((x) => x !== k))} data-testid={`editor-need-${k}`} />{v}</label>
            ))}</div>
          </div>
          <Formats formats={p.formats} onChange={set("formats")} />
          <Images images={p.images} onChange={set("images")} />
          {[["short", "Resumen"], ["description", "Descripción"], ["composition", "Composición"], ["usage", "Modo de empleo"]].map(([k, l]) => (
            <div key={k}><Label>{l}</Label><Textarea rows={k === "description" ? 4 : 2} value={p[k]} onChange={(e) => set(k)(e.target.value)} data-testid={`editor-${k}-input`} className="mt-1" /></div>
          ))}
          <div><Label>Dosis orientativa (calculadora y guía)</Label><Input value={p.dose} onChange={(e) => set("dose")(e.target.value)} data-testid="editor-dose-input" className="mt-1" /></div>
          <div className="flex flex-wrap gap-6">
            <label className="flex items-center gap-2 text-sm"><Switch checked={p.featured} onCheckedChange={set("featured")} data-testid="editor-featured-switch" /> Destacado en portada</label>
            <label className="flex items-center gap-2 text-sm"><Switch checked={p.hidden} onCheckedChange={set("hidden")} data-testid="editor-hidden-switch" /> Oculto en la tienda</label>
          </div>
          {p.featured && <div><Label>Texto destacado</Label><Input value={p.featured_text} onChange={(e) => set("featured_text")(e.target.value)} data-testid="editor-featured-text-input" className="mt-1" /></div>}
          <button disabled={busy} data-testid="editor-save-button" className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white font-display font-bold text-lg py-3 rounded-full">
            {busy ? "Guardando…" : "Guardar producto"}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
